import React, { useEffect, useState } from 'react';
import { Form, Switch } from 'antd';
import LoadingButton from '../../LoadingButton';
import ThemePreview from './ThemePreview';
import { ITheme, ThemePreset } from './themeType';
import ThemePresetCard from './ThemePresetCard';
import ColorPickerItem from './ColorPickerItem';
import { themePresets } from '../../../constants/themeTypes';

interface ThemePresetSelectorProps {
  onSubmit: (values: any, form: any) => void;
  initialValue?: ITheme,
  loading: boolean;
}

const defaultTheme: ITheme = {
  primary: '',
  primaryText: '',
  secondary: '',
  secondaryText: '',
  background: '',
  sidebarBackground: '',
  text: '',
  buttonText: '',
};

const ThemePresetSelector: React.FC<ThemePresetSelectorProps> = ({
  onSubmit,
  initialValue,
  loading,
}) => {
  const [form] = Form.useForm();
  const [customVisible, setCustomVisible] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ThemePreset>(themePresets[0]);
  const [customColors, setCustomColors] = useState<ITheme>(defaultTheme);
  const [openPicker, setOpenPicker] = useState<null | keyof ITheme>(null);

  const applyTheme = (colors: Record<string, string>) => {
    Object.entries(colors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
  };

  useEffect(() => {
    const savedThemeId = localStorage.getItem('selectedThemeId');
    const savedCustomVisible = localStorage.getItem('customVisible') === 'true';

    const preset = themePresets.find(p => p.id === savedThemeId) || themePresets[0];

    const appliedColors: ITheme = {
      ...defaultTheme,
      ...preset.colors,
      ...initialValue,
    };

    setSelectedTheme(preset);
    setCustomColors(appliedColors);
    setCustomVisible(savedCustomVisible);
  }, []);

  const handleColorChange = (color: string, type: keyof ITheme) => {
    const updatedColors = { ...customColors, [type]: color };
    setCustomColors(updatedColors);
    document.documentElement.style.setProperty(`--${type}`, color);
  };

  const handleThemeSelect = (preset: ThemePreset) => {
    setSelectedTheme(preset);
    setCustomVisible(false);
    setCustomColors(preset.colors);
    localStorage.setItem('selectedThemeId', preset.id);
    localStorage.setItem('customVisible', 'false');
  };

  const getFullTheme = (partial: Partial<ITheme>): ITheme => {
    return {
      ...defaultTheme,
      ...partial,
    };
  };

  const handleToggleChange = (checked: boolean) => {
    setCustomVisible(checked);
    localStorage.setItem('customVisible', String(checked));
    if (checked) {
      setCustomColors(getFullTheme(customColors));
    }
  };

  return (
    <div>
      <Form
        form={form}
        onFinish={() => {
          onSubmit({ branding: customColors }, form);
        }}
        layout="vertical"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          {themePresets.map((preset) => (
            <ThemePresetCard
              key={preset.id}
              id={preset.id}
              name={preset.name}
              colors={preset.colors}
              isSelected={!customVisible && selectedTheme.id === preset.id}
              onClick={() => handleThemeSelect(preset)}
            />
          ))}
        </div>

        <div className="mb-4">
          <span>Customize Theme: </span>
          <Switch
            checked={customVisible}
            onChange={handleToggleChange}
          />
        </div>
        {customVisible ? (
          <div className="flex justify-start gap-8 mb-6">
            <div className="space-y-4 w-full max-w-xs">
             {Object.entries(customColors).map(([key, color]) => (
                <ColorPickerItem
                  key={key}
                  type={key as keyof ITheme}
                  color={color}
                  open={openPicker === key}
                  onClick={() => setOpenPicker(openPicker === key ? null : (key as keyof ITheme))}
                  onChange={handleColorChange}
                />
              ))} 
            </div>

            <div className="w-[450px]">
              <ThemePreview colors={customColors} />
            </div>
          </div>
        ) : (
          <div className="w-[450px]">
            <ThemePreview colors={customColors} />
          </div>
        )}
        <Form.Item style={{ marginTop: 16 }}>
          <LoadingButton loading={loading} title="Set Theme" />
        </Form.Item>
      </Form>
    </div>
  );
};

export default ThemePresetSelector;
